import { Stack } from '@mui/material';
import { AddButton, CalculusActionPortal } from '@retail/components';
import { OutlinedButton } from '@shared/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsNew } from '@retail/calculus/types';
import { useDisclosure } from '@shared/hooks';
import { useState } from 'react';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { AgreementBinding } from '@retail/purchase-agreements/types';
import { useLanguageSelector } from '@retail/app/hooks';
import { PriceComparisonDialog } from '@retail/purchase-agreements/containers';
import { PurchaseConditionActions } from './PurchaseConditionActions';
import { PurchaseConditionTable } from '@retail/purchase-agreements/components';

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  supplierNumber?: number;
}

export function PurchaseConditionsContainer({ conditions, isFetching, supplierNumber }: Props) {
  const t = useCalculusTFunction();
  const { language } = useLanguageSelector();

  const { userCanCreate, userCanUpdate } = usePermissionsForCurrentUser();
  const canCreatePurchaseConditions = userCanCreate('PRICE_CALCULATION__PURCHASE_CONDITION');
  const canUsePurchaseConditionActions = userCanUpdate('PRICE_CALCULATION__PURCHASE_CONDITION');

  const { isOpen: createCondition, onToggle: toggleCreateDialog } = useDisclosure();
  const [priceComparison, setPriceComparison] = useState<AgreementBinding | undefined>(undefined);

  const [editCondition, setEdit] = useState<ConditionsNew | undefined>();
  const [deactivateConditionId, setDeactivateId] = useState<number | undefined>();
  const [deleteConditionId, setDeleteId] = useState<number | undefined>();

  const conditionActions =
    canUsePurchaseConditionActions && supplierNumber
      ? { setDeactivateId, setEdit, setDeleteId }
      : undefined;

  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        {supplierNumber ? (
          <OutlinedButton
            size="small"
            color="secondary"
            onClick={() => setPriceComparison({ supplier: { supplierNumber }, supplierNumber })}
          >
            {t('calculus.actions.comparePriceLists')}
          </OutlinedButton>
        ) : (
          <OutlinedButton size="small" disabled>
            {t('calculus.actions.comparePriceLists')}
          </OutlinedButton>
        )}

        <AddButton
          onClick={toggleCreateDialog}
          disabled={!canCreatePurchaseConditions || !supplierNumber}
        >
          {t('calculus.newPurchaseCondition')}
        </AddButton>
      </CalculusActionPortal>

      <PurchaseConditionTable
        conditions={conditions}
        language={language}
        loading={isFetching}
        conditionActions={conditionActions}
        setPriceComparisonBinding={setPriceComparison}
        openCreateDialog={canCreatePurchaseConditions ? toggleCreateDialog : undefined}
      />
      {supplierNumber && (
        <PurchaseConditionActions
          supplierNumber={supplierNumber}
          createCondition={createCondition}
          conditions={conditions}
          editCondition={editCondition}
          deactivateConditionId={deactivateConditionId}
          deleteConditionId={deleteConditionId}
          closeCreate={toggleCreateDialog}
          closeEdit={() => setEdit(undefined)}
          closeDeactivate={() => setDeactivateId(undefined)}
          closeDelete={() => setDeleteId(undefined)}
        />
      )}

      {priceComparison && priceComparison.supplierNumber && (
        <PriceComparisonDialog
          open
          onClose={() => setPriceComparison(undefined)}
          defaultAgreementId={priceComparison.agreementId}
          supplierNumber={priceComparison.supplierNumber}
          levelProductGroupNr={priceComparison.productGroup?.groupNumber}
          levelMgItemNr={priceComparison.legacySupplierItem?.mgItemNumber}
        />
      )}
    </Stack>
  );
}
